import React, { useEffect } from 'react';
import { LoggerFactory } from '@cvent/logging/LoggerFactory';
import useMeQuery from '@hooks/useMeQuery';
import { EngagementMessagesProvider } from '@components/engagement/EngagementMessages';
import { ENGAGEMENT_PANEL_IDS } from '@components/engagement/constants';
import UserActivityContextProvider from '@context/UserActivityContext';
import UserActivityHeartbeat from '@components/app/UserActivityHeartbeat';
import useLocaleForQueries from '@hooks/useLocaleForQueries';
import useDefaultTimeZone from '@hooks/useDefaultTimeZone';
import useAppFeatures from '@hooks/useAppFeatures';
import getConfig from 'next/config';
import { usePageActions } from '@cvent/sli-nextjs-metrics';
import useCompliance from './useCompliance';
import { apolloCacheLocaleVar } from '../../apollo/cache';
import { getLocaleFromCultureCode, validateLocaleCookie } from '../../../locales';
import { updateLegacyLoggerContext } from '../../config/legacyLogging';
import LocalizationProvider from './LocalizationProvider';
import { useAppReducer } from './AppReducer';
import { useAnalyticsProvider } from './useAnalyticsProvider';
import { useRouterTracking } from './useRouterTracking';
import PageTakeover from './PageTakeover';
import { AppProps } from './types';

const { publicRuntimeConfig } = getConfig();
const LOG = LoggerFactory.create('AppPage');

/**
 * Bootstrap common pages with providers and initial data fetching
 */
function AppPage({
  children,
  contactId,
  accountMappingId,
  httpLogRequestId,
  isGPCEnabled
}: React.PropsWithChildren<AppProps>): JSX.Element {
  const { state: { locale: currentLocale } = { locale: undefined } } = useAppReducer();
  let locale = currentLocale;

  // Set the apollo cache locale var which is getting used for custom cache key
  // this locale would be null when attendee hub multilingual experiment is off and avoid appending of locale param to cache id
  const localeForApolloCache = useLocaleForQueries();
  apolloCacheLocaleVar(localeForApolloCache);
  // Load application state from Apollo cache

  const {
    data: {
      me: {
        id: attendeeId,
        doNotSell,
        event: {
          id: eventId,
          codeSnippets,
          cultureCode,
          supportedEventLocales,
          title,
          start,
          end,
          status,
          links: { ccpa }
        } = {
          id: undefined,
          codeSnippets: undefined,
          cultureCode: undefined,
          supportedEventLocales: undefined,
          showCookieBannerPrivacyPolicyLink: false,
          links: { ccpa: undefined }
        }
      } = { id: undefined }
    } = {}
  } = useMeQuery();

  const appFeatures = useAppFeatures();

  // Update the current metric data with the event info
  const { updateMetricData } = usePageActions();
  updateMetricData({
    eventStatus: status,
    eventName: title,
    eventStartDate: start,
    eventEndDate: end,
    hasCodeSnippets: codeSnippets?.length > 0
  });

  useCompliance({
    globalPrivacyControl: appFeatures?.globalPrivacyControl,
    ccpa,
    isGPCEnabled,
    doNotSell,
    attendeeId,
    contactId
  });

  if (cultureCode) {
    // perform cookie validation
    locale = validateLocaleCookie(supportedEventLocales, locale);
    // App will be localised to the culture code if the current language equals event's base language
    // for eg if culture code is en-GB and current locale is en-US, app will be localised in en-GB, for other scenarios,
    // the current locale value would be used
    locale = getLocaleFromCultureCode(cultureCode, locale);
  }
  useEffect(() => {
    if (locale) {
      // log any change in the locale
      LOG.debug(`The app is localised in locale ${locale}`);
    }
  }, [locale]);

  // Make the logger aware of context
  useEffect(() => {
    updateLegacyLoggerContext({
      accountMappingId,
      eventId,
      attendeeId,
      httpLogRequestId
    });
  }, [accountMappingId, eventId, attendeeId, httpLogRequestId]);

  useDefaultTimeZone();

  useAnalyticsProvider({ locale, contactId, accountMappingId });
  useRouterTracking();

  return (
    <LocalizationProvider locale={locale}>
      <EngagementMessagesProvider
        panelIds={[
          ENGAGEMENT_PANEL_IDS.LIVE_QA,
          ENGAGEMENT_PANEL_IDS.LIVE_POLLING,
          ENGAGEMENT_PANEL_IDS.SURVEY,
          ENGAGEMENT_PANEL_IDS.CHAT,
          ENGAGEMENT_PANEL_IDS.AGENDA_ITEM_REMINDERS,
          ENGAGEMENT_PANEL_IDS.PLANNER_NOTIFICATIONS,
          ENGAGEMENT_PANEL_IDS.MEET_NOW,
          ENGAGEMENT_PANEL_IDS.INBOUND_LEADS,
          ENGAGEMENT_PANEL_IDS.SCHEDULE,
          ENGAGEMENT_PANEL_IDS.CODE_WIDGETS,
          ENGAGEMENT_PANEL_IDS.MODERATOR_ENGAGEMENT
        ]}
      >
        <UserActivityContextProvider>
          <UserActivityHeartbeat
            heartbeatFrequencyInSeconds={Number(publicRuntimeConfig.HEARTBEAT_FREQUENCY_IN_SECONDS) || 60}
          />
          <PageTakeover>{children}</PageTakeover>
        </UserActivityContextProvider>
      </EngagementMessagesProvider>
    </LocalizationProvider>
  );
}

export default AppPage;
